import React, { useContext, useState, useCallback, useRef, useEffect, useMemo } from "react";
import AddGroup from "../Groups/AddGroup";
import {
  IGroupValue,
  IContextVacancies,
  ContextVacancies,
  ILeague,
} from "../contextVacancies/ContextVacancies";
import { VacanciesType } from "../Vacancies";
import { ContextApp, IContextApp } from "@config/Context/contextApp";
import LinkCard from "./components/link-card";
import GroupMigrationDrawer from "./components/group-migration-drawer";
import LinkClientCodeDrawer from "./components/link-client-code-drawer";

export interface ICardVacanciesProps {
  data: Array<ILeague>;
  activeEdit: boolean;
  setValidationArray?: any;
  type: VacanciesType;
  nameGroup?: string;
  bundleId: number | string;
  editingLinkId?: string;
}

/**
 * Updates a specific link's group values in the context state using a functional updater.
 *
 * Using a functional updater (`setter(prev => ...)`) is intentional: `onChange` for group
 * inputs is called inside a `setCountVacancies` state updater in `useGroupsInputState`, which
 * means React may batch this `setChangeValues` call together with the parent update. In that
 * batched execution, any snapshot of `changeValues` read from a ref or closure would be stale.
 * The functional updater guarantees that `prev` is always the latest committed state,
 * regardless of batching or rendering order.
 *
 * Insertion vs. replacement is determined by `index` rather than a `dataLength` comparison.
 * The length-based heuristic (`dataLength > countContext → return [value]`) was incorrect:
 * it always produced a single-element array, losing any previously inserted link values.
 * Using `index < clone.length` correctly handles both initial population (insert at position)
 * and subsequent updates (replace at position), regardless of the number of links.
 *
 * @param value - The updated array of group values for the given link index.
 * @param index - The index of the link — used to insert or replace at the correct position.
 * @param setter - The context state setter — must accept a functional updater `(prev) => newState`.
 */
const applyValuesChange = (
  value: Array<IGroupValue>,
  index: number,
  setter: (updater: (prev: Array<Array<IGroupValue>> | null) => Array<Array<IGroupValue>>) => void,
) => {
  if (!value) return;
  setter(prev => {
    const clone = [...(prev ?? [])];
    if (index < clone.length) {
      clone.splice(index, 1, value);
    } else {
      clone.splice(index, 0, value);
    }
    return clone;
  });
};

const Links: React.FC<ICardVacanciesProps> = ({ data, ...props }: ICardVacanciesProps) => {
  const { activeEdit, setValidationArray, type, nameGroup, bundleId, editingLinkId } = props;
  const contextApp: IContextApp = useContext(ContextApp);
  const context: IContextVacancies = useContext(ContextVacancies);
  const [responsibilityChange, setResponsibilityChange] = useState<Array<Array<IGroupValue>>>([]);
  const refContext = useRef(context);
  refContext.current = context;
  const [openGroup, setOpenGroup] = useState(false);
  const [groupToMigrate, setGroupToMigrate] = useState<string | number | null>(null);
  const [linkId, setLinkId] = useState(null);
  const [subTitle, setSubtitle] = useState(null);
  const [showClientCodeDrawer, setShowClientCodeDrawer] = useState(false);

  const userCanEdit =
    contextApp?.user?.permissions?.update && contextApp?.user?.abilities?.can_edit_assignment;

  const someLinksGroupsHasSectionCode = useMemo(() => {
    return data?.some((item: ILeague) => {
      return item.groups?.some(group => group?.groupSections?.some(section => section.code));
    });
  }, [data]);

  const selectedLinkDetails = useMemo(() => {
    return [...(data ?? [])].find((item: ILeague) => item.linkId === linkId);
  }, [data, linkId]);

  const selectedGroupDetails = useMemo(() => {
    return selectedLinkDetails?.groups?.find(group => group.id === groupToMigrate);
  }, [selectedLinkDetails, groupToMigrate]);

  /**
   * Propagates vacancies input changes for a specific link to the shared context.
   * Delegates to `applyValuesChange` which uses a functional updater to avoid stale state.
   */
  const setValuesChange = useCallback(
    (value: Array<IGroupValue>, index: number) => {
      applyValuesChange(value, index, refContext.current.setChangeValues);
    },
    [refContext],
  );

  /**
   * Propagates retaker capacity input changes for a specific link to the shared context.
   * Delegates to `applyValuesChange` which uses a functional updater to avoid stale state.
   */
  const setRetakerValuesChange = useCallback(
    (value: Array<IGroupValue>, index: number) => {
      applyValuesChange(value, index, refContext.current.setRetakerChangeValues);
    },
    [refContext],
  );

  const setValuesResponsability = (value: Array<IGroupValue>) => {
    setResponsibilityChange(previous => {
      const linkId = value?.[0]?.linkId;
      const existingIndex = previous.findIndex(values => values?.[0]?.linkId === linkId);

      if (existingIndex < 0) return [...previous, value];

      const next = [...previous];
      next.splice(existingIndex, 1, value);
      return next;
    });
  };

  const handleOpenGroup = (link: string) => {
    setOpenGroup(true);
    setLinkId(link);
  };

  useEffect(() => {
    context.setInstructorValues(responsibilityChange);
  }, [responsibilityChange]);

  useEffect(() => {
    if (!activeEdit) {
      setResponsibilityChange([]);
    }
  }, [activeEdit]);

  return (
    <>
      {!openGroup &&
        data &&
        data.map((item: ILeague, index: number) => {
          return (
            <LinkCard
              key={index}
              link={item}
              userCanEdit={userCanEdit}
              tableType={type}
              isEditing={activeEdit && (!editingLinkId || editingLinkId === item.linkId)}
              hasGroupSections={someLinksGroupsHasSectionCode}
              setValidationArray={setValidationArray}
              onUpdateGroupValues={values => setValuesChange(values, index)}
              onUpdateRetakerValues={values => setRetakerValuesChange(values, index)}
              onUpdateInstructorValues={setValuesResponsability}
              onCreateGroup={() => {
                handleOpenGroup(item.linkId);
                setSubtitle(item.code);
              }}
              onMigrateGroup={groupId => {
                setLinkId(item.linkId);
                setGroupToMigrate(groupId);
              }}
              onAssignClientCode={() => {
                setLinkId(item.linkId);
                setShowClientCodeDrawer(true);
              }}
            />
          );
        })}
      {openGroup && (
        <AddGroup
          onClose={() => setOpenGroup(false)}
          linkId={linkId}
          bundleId={bundleId}
          headerData={{ subTitle: subTitle, title: nameGroup }}
        />
      )}

      <GroupMigrationDrawer
        isOpen={!!groupToMigrate}
        onClose={() => setGroupToMigrate(null)}
        linkDetails={selectedLinkDetails}
        groupDetails={selectedGroupDetails}
      />

      <LinkClientCodeDrawer
        isOpen={showClientCodeDrawer}
        linkDetails={selectedLinkDetails}
        onClose={() => setShowClientCodeDrawer(false)}
      />
    </>
  );
};

export default Links;
